class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.discountMessageState = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#header-cart-icon');
    const cartLinkNav = document.querySelector('#header-cart-icon-nav');
    if (!cartLink) return;

    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    cartLinkNav?.setAttribute('role', 'button');
    cartLinkNav?.setAttribute('aria-haspopup', 'dialog');
    cartLinkNav?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLinkNav);
    });
  }

  open(triggeredBy) {
    // Initialize debug array if not exists
    if (!window.DEBUGGING) window.DEBUGGING = [];
    
    window.DEBUGGING.push({
      type: 'cartDrawer_open',
      triggeredBy: triggeredBy?.id || 'unknown',
      timestamp: Date.now()
    });

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body?.classList.add('overflow-hidden');
    
    this.initDiscountCode();
    
    // Initialize product carousel when drawer opens
    this.initProductCarousel();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body?.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote?.setAttribute('role', 'button');
    cartDrawerNote?.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote?.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote?.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote?.addEventListener('click', (event) => {
      event.currentTarget?.setAttribute('aria-expanded', !event.currentTarget?.closest('details')?.hasAttribute('open'));
    });

    cartDrawerNote?.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!window.DEBUGGING) window.DEBUGGING = [];
    
    window.DEBUGGING.push({
      type: 'cartDrawer_renderContents',
      productId: parsedState.id,
      sections: Object.keys(parsedState.sections || {}),
      timestamp: Date.now()
    });

    this.querySelector('.drawer__inner')?.classList.contains('is-empty') &&
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) {
        window.DEBUGGING.push({
          type: 'cartDrawer_missing_section',
          section,
          timestamp: Date.now()
        });
        return;
      }
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();

      this.dispatchEvent(new CustomEvent('cart-drawer-update', {
        detail: {
          cartData: parsedState,
          timestamp: Date.now(),
          source: 'cart-drawer'
        },
        bubbles: true
      }));
      
      publish(PUB_SUB_EVENTS.cartDrawerUpdate, {
        cartData: parsedState,
        timestamp: Date.now(),
        source: 'cart-drawer'
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector) || null;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initDiscountCode() {
    const discountInput = this.querySelector('#CartDrawer-Discount');
    const applyButton = this.querySelector('#CartDrawer-Discount-Apply');
    const messageElement = this.querySelector('#CartDrawer-Discount-Message');

    if (!discountInput || !applyButton || !messageElement) return;
    if (discountInput.dataset.initialized === 'true') return;

    if (!window.DEBUGGING) window.DEBUGGING = [];
    
    window.DEBUGGING.push({
      type: 'cartDrawer_initDiscount',
      timestamp: Date.now()
    });

    discountInput.dataset.initialized = 'true';

    this.restoreDiscountMessage();

    applyButton.addEventListener('click', () => {
      this.applyDiscountCode();
    });

    discountInput.addEventListener('keypress', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyDiscountCode();
      }
    });

    discountInput.addEventListener('input', () => {
      if (messageElement.innerHTML.includes('error')) {
        this.clearDiscountMessage();
      }
    });

    let validationTimeout;
    discountInput.addEventListener('input', () => {
      clearTimeout(validationTimeout);
      const code = discountInput.value.trim();

      if (code.length >= 3) {
        validationTimeout = setTimeout(() => {
          const existingDiscounts = this.getAppliedDiscounts();
          if (existingDiscounts.some(discount => discount.toLowerCase() === code.toLowerCase())) {
            this.showDiscountMessage('This discount is already applied', 'error');
          }
        }, 1000);
      }
    });
  }

  async applyDiscountCode() {
    const discountInput = this.querySelector('#CartDrawer-Discount');
    const applyButton = this.querySelector('#CartDrawer-Discount-Apply');
    const messageElement = this.querySelector('#CartDrawer-Discount-Message');

    if (!discountInput || !applyButton || !messageElement) return;

    const code = discountInput.value.trim();
    
    if (!code) {
      this.showDiscountMessage('Please enter a discount code', 'error');
      return;
    }

    let lastAttemptTime = window.lastDiscountAttempt || 0;
    let attemptCount = window.discountAttemptCount || 0;
    const now = Date.now();
    const RATE_LIMIT_DELAY = 2000;
    const MAX_ATTEMPTS = 5;
    const RESET_WINDOW = 60000;

    if (now - lastAttemptTime > RESET_WINDOW) attemptCount = 0;

    if (attemptCount >= MAX_ATTEMPTS) {
      this.showDiscountMessage('Too many attempts. Please wait before trying again.', 'error');
      applyButton.disabled = true;
      discountInput.disabled = true;

      setTimeout(() => {
        window.discountAttemptCount = 0;
        applyButton.disabled = false;
        discountInput.disabled = false;
        this.clearDiscountMessage();
      }, RESET_WINDOW - (now - lastAttemptTime));

      return;
    }

    if (now - lastAttemptTime < RATE_LIMIT_DELAY) {
      const remaining = Math.ceil((RATE_LIMIT_DELAY - (now - lastAttemptTime)) / 1000);
      this.showDiscountMessage(`Please wait ${remaining} seconds before trying another code.`, 'error');
      this.showRateLimitCountdown(remaining);
      return;
    }

    window.lastDiscountAttempt = now;
    window.discountAttemptCount = attemptCount + 1;

    if (!window.DEBUGGING) window.DEBUGGING = [];
    
    window.DEBUGGING.push({
      type: 'cartDrawer_applyDiscount',
      code: code,
      timestamp: Date.now()
    });

    this.setLoadingState(true);
    this.clearDiscountMessage();

    try {
      const cartBefore = await fetch('/cart.js').then(res => res.json());
      const validation = await this.validateDiscountCode(code);

      if (!validation.valid) {
        this.showDiscountMessage(validation.message, 'error');
        this.setLoadingState(false);
        return;
      }

      const applied = await this.applyDiscountCodeToCart(code, cartBefore);

      if (applied.success && applied.actualSavings > 0) {
        const successMessage = `Discount "${code}" applied successfully! You saved ${applied.savings}`;
        this.showDiscountMessage(successMessage, 'success');
        this.saveDiscountMessage(successMessage, 'success', code);
        discountInput.value = '';
        await this.refreshCartDrawer();
        
        this.dispatchEvent(new CustomEvent('cart-drawer-update', {
          detail: {
            cartData: applied.cartAfter || await fetch('/cart.js').then(res => res.json()),
            timestamp: Date.now(),
            source: 'discount-applied',
            discountCode: code,
            savings: applied.savings,
            actualSavings: applied.actualSavings
          },
          bubbles: true
        }));
        
        publish(PUB_SUB_EVENTS.cartDrawerUpdate, {
          cartData: applied.cartAfter || await fetch('/cart.js').then(res => res.json()),
          timestamp: Date.now(),
          source: 'discount-applied',
          discountCode: code,
          savings: applied.savings,
          actualSavings: applied.actualSavings
        });
      } else {
        this.showDiscountMessage('Invalid code or not applicable to any of the cart items.', 'error');
      }
    } catch (error) {
      console.error('Discount application error:', error);
      this.showDiscountMessage('Something went wrong. Please try again.', 'error');
    } finally {
      this.setLoadingState(false);
    }
  }

  showDiscountMessage(message, type) {
    const messageElement = this.querySelector('#CartDrawer-Discount-Message');
    if (!messageElement) return;

    messageElement.className = 'cart-drawer__discount-message';
    
    messageElement.classList.add(type);
    messageElement.textContent = message;
  }

  clearDiscountMessage() {
    const messageElement = this.querySelector('#CartDrawer-Discount-Message');
    if (messageElement) {
      messageElement.className = 'cart-drawer__discount-message';
      messageElement.textContent = '';
    }
  }

  refreshCart() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount' });
    }
  }

  async validateDiscountCode(code) {
    try {
      const cart = await fetch('/cart.js').then(res => res.json());
      const existingDiscounts = this.getAppliedDiscounts();
      
      if (existingDiscounts.some(d => d.toLowerCase() === code.toLowerCase())) {
        return { valid: false, message: 'This discount code is already applied to your cart' };
      }

      const resp = await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'HEAD' });
      if (resp.status === 404) {
        return { valid: false, message: 'Invalid discount code. Please check the code and try again.' };
      }

      if (cart.items.length === 0) {
        return { valid: false, message: 'Your cart is empty. Add items before applying a discount.' };
      }
      if (cart.total_price === 0) {
        return { valid: false, message: 'Discount cannot be applied to a cart with zero value.' };
      }

      return { valid: true };
    } catch {
      return { valid: false, message: 'Could not validate discount code. Try again later.' };
    }
  }

  async applyDiscountCodeToCart(code, cartBefore) {
    try {
      const res = await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        redirect: 'manual'
      });

      if (res.status === 302 || res.type === 'opaqueredirect') {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const cartAfter = window.BQGiftCart
          ? await window.BQGiftCart.sync(false)
          : await fetch('/cart.js').then(res => res.json());
        const diff = cartBefore.total_price - cartAfter.total_price;
        
        return {
          success: true,
          actualSavings: diff,
          savings: this.formatMoney(diff),
          message: 'Discount applied',
          cartAfter: cartAfter
        };
      } else {
        return { success: false, message: 'Discount code cannot be applied to current cart items', actualSavings: 0 };
      }
    } catch (error) {
      return { success: false, message: 'Error applying discount code', actualSavings: 0 };
    }
  }

  setLoadingState(loading) {
    const btn = this.querySelector('#CartDrawer-Discount-Apply');
    const input = this.querySelector('#CartDrawer-Discount');
    if (!btn || !input) return;

    btn.disabled = loading;
    input.disabled = loading;
    btn.textContent = loading ? 'Applying...' : 'Apply';
  }

  showRateLimitCountdown(seconds) {
    const btn = this.querySelector('#CartDrawer-Discount-Apply');
    const input = this.querySelector('#CartDrawer-Discount');
    const start = Date.now();

    const tick = () => {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      const remaining = seconds - elapsed;

      if (remaining > 0) {
        btn.textContent = `Wait ${remaining}s`;
        setTimeout(tick, 1000);
      } else {
        btn.disabled = false;
        input.disabled = false;
        btn.textContent = 'Apply';
      }
    };

    tick();
  }

  getAppliedDiscounts() {
    return Array.from(this.querySelectorAll('.discounts__discount')).map(el => {
      const discountCode = el.dataset.discountCode;
      return discountCode;
    }).filter(Boolean);
  }

  refreshCartDrawer() {
    return fetch(`${window.location.pathname}?sections=cart-drawer`)
      .then(res => res.json())
      .then(sections => {
        this.renderContents({ sections });
      })
      .catch(err => {
        window.DEBUGGING.push({
          type: 'cartDrawer_refreshError',
          error: err.message,
          timestamp: Date.now()
        });
      });
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  saveDiscountMessage(msg, type, code = '') {
    this.discountMessageState = { message: msg, type, timestamp: Date.now(), discountCode: code };
  }

  restoreDiscountMessage() {
    if (!this.discountMessageState) return;
    
    const { message, type, timestamp } = this.discountMessageState;
    const age = Date.now() - timestamp;
    const maxAge = 30000;

    if (message && age < maxAge) {
      this.showDiscountMessage(message, type);
      if (type === 'success') {
        setTimeout(() => this.clearDiscountMessage(), 8000 - age);
      }
    } else {
      this.discountMessageState = null;
    }
  }

// ==========================================
// PRODUCT CAROUSEL FUNCTIONALITY START
// ==========================================

initProductCarousel() {
    const carousel = this.querySelector('.cart-drawer__products-carousel');
    if (!carousel) return;

    if (carousel.dataset.initialized === 'true') return;
    carousel.dataset.initialized = 'true';

    const track = carousel.querySelector('.cart-drawer__products-track');
    if (!track) return;

    if (!window.DEBUGGING) window.DEBUGGING = [];
    
    window.DEBUGGING.push({
      type: 'cartDrawer_initProductCarousel',
      timestamp: Date.now()
    });

    this.setupCarouselScrollIndicator(carousel, track);
    this.setupCarouselAddButtons();
    this.setupCarouselTouchScroll(track);
  }

  setupCarouselScrollIndicator(carousel, track) {
    const checkScroll = () => {
      const isScrollable = track.scrollWidth > track.clientWidth;
      carousel.classList.toggle('has-scroll', isScrollable);
    };

    checkScroll();
    
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(carousel);
      resizeObserver.observe(track);
      
      this._carouselResizeObserver = resizeObserver;
    } else {
      const resizeHandler = () => checkScroll();
      window.addEventListener('resize', resizeHandler);
      this._carouselResizeHandler = resizeHandler;
    }
  }

  setupCarouselAddButtons() {
    const addButtons = this.querySelectorAll('.product-card__add-button');
    
    addButtons.forEach(button => {
      if (button.dataset.listenerAttached === 'true') return;
      button.dataset.listenerAttached = 'true';

      button.addEventListener('click', (e) => this.handleCarouselAddToCart(e));
    });
  }

  async handleCarouselAddToCart(event) {
    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const productTitle = button.dataset.productTitle;

    if (button.disabled) return;
    
    console.log('=== Add to Cart Clicked ===');
    console.log('Product ID:', productId);
    console.log('Variant ID:', variantId);
    console.log('Product Title:', productTitle);
    console.log('===========================');

    if (!window.DEBUGGING) window.DEBUGGING = [];
    
    window.DEBUGGING.push({
      type: 'cartDrawer_productCarouselAdd',
      productId: productId,
      variantId: variantId,
      productTitle: productTitle,
      timestamp: Date.now()
    });

    this.setCarouselButtonState(button, 'loading');

    try {
      const response = await (window.BQGiftCart?.fetch || fetch)(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();
      
      console.log('Successfully added to cart:', data);
      
      window.DEBUGGING.push({
        type: 'cartDrawer_productCarouselAddSuccess',
        productId: productId,
        variantId: variantId,
        data: data,
        timestamp: Date.now()
      });
      
      this.setCarouselButtonState(button, 'added');
      
      await this.refreshCartDrawer();
      
      this.dispatchEvent(new CustomEvent('cart-drawer-product-added', {
        detail: {
          productId: productId,
          variantId: variantId,
          productTitle: productTitle,
          cartData: data,
          timestamp: Date.now()
        },
        bubbles: true
      }));
      
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'cart-drawer-carousel',
          productAdded: {
            productId,
            variantId,
            productTitle
          }
        });
      }
      
      setTimeout(() => {
        this.setCarouselButtonState(button, 'default');
      }, 2000);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      window.DEBUGGING.push({
        type: 'cartDrawer_productCarouselAddError',
        productId: productId,
        variantId: variantId,
        error: error.message,
        timestamp: Date.now()
      });
      
      this.setCarouselButtonState(button, 'error');
      
      alert(error.message || 'Unable to add product to cart. Please try again.');
      
      setTimeout(() => {
        this.setCarouselButtonState(button, 'default');
      }, 2000);
    }
  }

  setCarouselButtonState(button, state) {
    button.classList.remove('loading', 'added', 'error');
    button.disabled = false;

    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent.trim();
    }
    
    const originalText = button.dataset.originalText;

    switch (state) {
      case 'loading':
        button.classList.add('loading');
        button.disabled = true;
        button.textContent = 'Adding...';
        break;
      
      case 'added':
        button.classList.add('added');
        button.textContent = '✓ Added';
        break;
      
      case 'error':
        button.classList.add('error');
        button.textContent = '✗ Error';
        break;
      
      case 'default':
      default:
        button.textContent = originalText || 'Add';
        break;
    }
  }

  setupCarouselTouchScroll(track) {
    const carousel = track.parentElement;
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let velocity = 0;
    let lastX = 0;
    let lastTime = Date.now();

    const updatePosition = (currentX) => {
      if (!isDragging) return;
      
      const deltaX = currentX - startX;
      const rawScrollLeft = scrollLeft - deltaX;
      
      const padding = 40;
      const maxScroll = Math.max(0, track.scrollWidth - carousel.offsetWidth + padding);
      const minScroll = 0;
      
      let newScrollLeft = rawScrollLeft;
      
      const overscrollLimit = 100;
      
      if (rawScrollLeft < minScroll) {
        const overscroll = minScroll - rawScrollLeft;
        const resistance = Math.min(overscroll, overscrollLimit) / overscrollLimit;
        newScrollLeft = minScroll - (overscroll * (1 - resistance * 0.7));
      } else if (rawScrollLeft > maxScroll) {
        const overscroll = rawScrollLeft - maxScroll;
        const resistance = Math.min(overscroll, overscrollLimit) / overscrollLimit;
        newScrollLeft = maxScroll + (overscroll * (1 - resistance * 0.7));
      }
      
      const now = Date.now();
      const deltaTime = now - lastTime;
      if (deltaTime > 0) {
        velocity = (currentX - lastX) / deltaTime;
      }
      lastX = currentX;
      lastTime = now;
      
      track.style.transform = `translateX(${-newScrollLeft}px)`;
    };


    const startDrag = (clientX) => {
      isDragging = true;
      startX = clientX;
      lastX = clientX;
      lastTime = Date.now();
      velocity = 0;
      
      const currentTransform = track.style.transform || 'translateX(0px)';
      scrollLeft = parseInt(currentTransform.match(/-?\d+/) || [0]) * -1;
      
      carousel.classList.add('is-dragging');
      track.classList.add('is-dragging');
    };

    const stopDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      
      carousel.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      
      const padding = 40;
      const maxScroll = Math.max(0, track.scrollWidth - carousel.offsetWidth + padding);
      
      if (Math.abs(velocity) > 0.5) {
        const momentum = velocity * 200;
        const currentTransform = track.style.transform || 'translateX(0px)';
        const currentPos = parseInt(currentTransform.match(/-?\d+/) || [0]);
        const newPos = Math.max(
          -maxScroll,
          Math.min(0, currentPos + momentum)
        );
        track.style.transition = 'transform 0.3s ease-out';
        track.style.transform = `translateX(${newPos}px)`;
        
        setTimeout(() => {
          track.style.transition = 'none';
        }, 300);
      }
    };

    carousel.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startDrag(e.clientX);
    });

    carousel.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      updatePosition(e.clientX);
    });

    carousel.addEventListener('mouseup', stopDrag);
    carousel.addEventListener('mouseleave', stopDrag);

    carousel.addEventListener('touchstart', (e) => {
      startDrag(e.touches[0].clientX);
    }, { passive: true });

    carousel.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      updatePosition(e.touches[0].clientX);
    }, { passive: true });

    carousel.addEventListener('touchend', stopDrag, { passive: true });

    carousel.addEventListener('dragstart', (e) => e.preventDefault());

    carousel.addEventListener('click', (e) => {
      if (Math.abs(velocity) > 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, { capture: true });
  }
}
  // ==========================================
  // PRODUCT CAROUSEL FUNCTIONALITY END
  // ==========================================

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', function() {
  var cartButton = document.getElementById('header-cart-icon');
  var cartButtonNav = document.getElementById('header-cart-icon-nav');
  var cartDrawer = document.querySelector('cart-drawer');
  if (cartButton && cartDrawer) {
    cartButton?.addEventListener('click', function (e) {
      e.preventDefault();
      cartDrawer?.open(cartButton);
    });

    cartButtonNav?.addEventListener('click', function (e) {
      e.preventDefault();
      cartDrawer?.open(cartButtonNav);
    });
    
    // Disable cart-summary when cart drawer is enabled
    const cartSummary = document.querySelector('.cart-summary');
    if (cartSummary) {
      cartSummary.style.display = 'none';
    }
  }
});